(() => {
  'use strict';

  const CONTEXT_SELECTOR = '[data-bdk-collection-context]';
  const NATIVE_FILTER_SELECTOR = '[data-bdk-native-filter-toggle]';
  const PROXY_SELECTOR = '[data-bdk-filter-proxy]';
  const MOBILE_BREADCRUMB_SELECTOR = '[data-bdk-collection-mobile-context] .bdk-category-breadcrumb';
  const LOCAL_NAV_SELECTOR = '[data-bdk-local-nav-scroll]';
  const LOCAL_NAV_VIEWPORT_SELECTOR = '[data-bdk-local-nav-viewport]';
  const LOCAL_NAV_PREV_SELECTOR = '[data-bdk-local-nav-prev]';
  const LOCAL_NAV_NEXT_SELECTOR = '[data-bdk-local-nav-next]';
  const LOCAL_NAV_ACTIVE_SELECTOR = '[data-bdk-local-nav-active], .subcollections-inline__link.is-active';
  const COLLECTION_SLIDER_SELECTOR = '[data-grid-slider]';
  const COLLECTION_SLIDER_CURRENT_SELECTOR = '[data-bdk-collection-slider-current]';
  const DESKTOP_TOPBAR_SELECTOR = '.bdk-collection-context__topbar';
  const DESKTOP_STICKY_SENTINEL_SELECTOR = '[data-bdk-desktop-sticky-sentinel]';
  const DESKTOP_QUERY = '(min-width: 750px)';
  const LOCAL_NAV_MOBILE_QUERY = '(max-width: 989px)';

  let rewardsLauncherState = null;
  let rewardsHideTimers = [];
  let desktopStickyBar = null;
  let desktopStickySentinel = null;
  let desktopStickyFrame = null;

  const findInOpenRoots = (root, selector) => {
    if (!root?.querySelector) return null;

    const direct = root.querySelector(selector);
    if (direct) return direct;

    for (const element of root.querySelectorAll('*')) {
      if (!element.shadowRoot) continue;
      const nested = findInOpenRoots(element.shadowRoot, selector);
      if (nested) return nested;
    }

    return null;
  };

  const clearRewardsHideTimers = () => {
    rewardsHideTimers.forEach((timer) => window.clearTimeout(timer));
    rewardsHideTimers = [];
  };

  const hideRewardsLauncher = () => {
    const cached = rewardsLauncherState?.element;
    const launcher = cached?.isConnected
      ? cached
      : findInOpenRoots(document, '.gw-rd-popup-widget-launcher-btn');

    if (!(launcher instanceof HTMLElement)) return;

    if (!rewardsLauncherState || rewardsLauncherState.element !== launcher) {
      const properties = ['visibility', 'opacity', 'pointer-events'];
      const values = {};

      properties.forEach((property) => {
        values[property] = {
          value: launcher.style.getPropertyValue(property),
          priority: launcher.style.getPropertyPriority(property),
        };
      });

      rewardsLauncherState = { element: launcher, values };
    }

    launcher.style.setProperty('visibility', 'hidden', 'important');
    launcher.style.setProperty('opacity', '0', 'important');
    launcher.style.setProperty('pointer-events', 'none', 'important');
    launcher.setAttribute('data-bdk-hidden-by-filters', 'true');
  };

  const restoreRewardsLauncher = () => {
    clearRewardsHideTimers();
    const state = rewardsLauncherState;
    if (!state?.element) return;

    Object.entries(state.values).forEach(([property, stored]) => {
      if (stored.value) state.element.style.setProperty(property, stored.value, stored.priority || '');
      else state.element.style.removeProperty(property);
    });

    state.element.removeAttribute('data-bdk-hidden-by-filters');
    rewardsLauncherState = null;
  };

  const syncRewardsLauncher = (sidebar) => {
    clearRewardsHideTimers();

    if (!sidebar?.classList.contains('expanded')) {
      restoreRewardsLauncher();
      return;
    }

    [0, 200, 650, 1500].forEach((delay) => {
      rewardsHideTimers.push(window.setTimeout(() => {
        if (sidebar.classList.contains('expanded')) hideRewardsLauncher();
      }, delay));
    });
  };

  const syncProxyState = (nativeToggle, proxies) => {
    if (!nativeToggle) return;

    const expanded = nativeToggle.getAttribute('aria-expanded') === 'true';
    const nativeCount = nativeToggle.querySelector('[data-active-filters]');
    const countText = nativeCount?.textContent?.trim() || '0';
    const hasCount = Number.parseInt(countText, 10) > 0;

    proxies.forEach((proxy) => {
      proxy.setAttribute('aria-expanded', expanded ? 'true' : 'false');

      const proxyLabel = proxy.querySelector('[data-bdk-filter-proxy-label]');
      if (proxyLabel) {
        const showLabel = proxy.dataset.bdkFilterLabelShow || proxyLabel.textContent.trim();
        const hideLabel = proxy.dataset.bdkFilterLabelHide || showLabel;
        proxyLabel.textContent = expanded ? hideLabel : showLabel;
      }

      const proxyCount = proxy.querySelector('[data-bdk-filter-proxy-count]');
      if (!proxyCount) return;

      proxyCount.textContent = countText;
      proxyCount.classList.toggle('hidden', !hasCount);
    });
  };

  const bindProxy = (proxy, nativeToggle) => {
    if (!proxy || !nativeToggle || proxy.dataset.bdkFilterProxyBound === 'true') return;

    proxy.dataset.bdkFilterProxyBound = 'true';
    proxy.addEventListener('click', () => {
      nativeToggle.click();
      window.requestAnimationFrame(() => syncProxyState(nativeToggle, [proxy]));
    });
  };

  const normalizeBreadcrumbText = (text) => {
  return (text || '').replace(/\s+/g, ' ').trim().toLocaleLowerCase();
  };

  const syncBreadcrumbDuplicateNode = (context) => {
    const breadcrumb = context.querySelector('.bdk-collection-context__breadcrumb');
    const node = breadcrumb?.querySelector('[data-bdk-breadcrumb-taxonomy-node]');
    const separator = breadcrumb?.querySelector('[data-bdk-breadcrumb-node-separator]');
    const current = breadcrumb?.querySelector('.bdk-category-breadcrumb__current');

    if (!node || !current) return;

    const duplicate = normalizeBreadcrumbText(node.textContent) === normalizeBreadcrumbText(current.textContent);

    node.hidden = duplicate;
    if (separator) separator.hidden = duplicate;
  };
  const getDesktopStickyOffset = () => {
    if (!document.querySelector('[data-header-sticky]')) return 0;

    const styles = window.getComputedStyle(document.documentElement);
    const announcementHeight = Number.parseFloat(styles.getPropertyValue('--announcement-height')) || 0;
    const headerHeight = Number.parseFloat(styles.getPropertyValue('--header-height')) || 0;
    return announcementHeight + headerHeight;
  };

  const resetDesktopStickyBar = () => {
    desktopStickyBar?.classList.remove('is-desktop-sticky');
    desktopStickyBar?.style.removeProperty('--bdk-context-sticky-top');
    if (desktopStickySentinel) desktopStickySentinel.style.height = '0px';
  };

  const syncDesktopStickyBar = (context) => {
    const topbar = context?.querySelector(DESKTOP_TOPBAR_SELECTOR);
    if (!topbar) {
      resetDesktopStickyBar();
      desktopStickyBar = null;
      desktopStickySentinel = null;
      return;
    }

    if (desktopStickyBar !== topbar) {
      resetDesktopStickyBar();
      desktopStickyBar = topbar;
      desktopStickySentinel = topbar.previousElementSibling?.matches(DESKTOP_STICKY_SENTINEL_SELECTOR)
        ? topbar.previousElementSibling
        : document.createElement('div');

      if (!desktopStickySentinel.matches(DESKTOP_STICKY_SENTINEL_SELECTOR)) {
        desktopStickySentinel.setAttribute('data-bdk-desktop-sticky-sentinel', '');
        desktopStickySentinel.setAttribute('aria-hidden', 'true');
        topbar.before(desktopStickySentinel);
      }
    }

    if (!window.matchMedia(DESKTOP_QUERY).matches) {
      resetDesktopStickyBar();
      return;
    }

    const stickyOffset = getDesktopStickyOffset();
const sentinelTop = desktopStickySentinel.getBoundingClientRect().top;
const isSticky = topbar.classList.contains('is-desktop-sticky');
const shouldStick = window.scrollY > 0 && sentinelTop <= stickyOffset;
const topbarHeight = topbar.getBoundingClientRect().height;

topbar.style.setProperty('--bdk-context-sticky-top', `${stickyOffset}px`);

if (shouldStick === isSticky) {
  if (shouldStick) {
    const sentinelHeight = Number.parseFloat(desktopStickySentinel.style.height) || 0;

    if (Math.abs(sentinelHeight - topbarHeight) > 0.1) {
      desktopStickySentinel.style.height = `${topbarHeight}px`;
    }
  }

  return;
}

if (shouldStick) {
  desktopStickySentinel.style.height = `${topbarHeight}px`;
  topbar.classList.add('is-desktop-sticky');
} else {
  topbar.classList.remove('is-desktop-sticky');
  desktopStickySentinel.style.height = '0px';
}
  };

  const scheduleDesktopStickyBar = () => {
    if (desktopStickyFrame) return;

    desktopStickyFrame = window.requestAnimationFrame(() => {
      desktopStickyFrame = null;
      syncDesktopStickyBar(document.querySelector(CONTEXT_SELECTOR));
    });
  };

  const syncMobileBreadcrumb = (context) => {
    const sourceBreadcrumb = context.querySelector('.bdk-collection-context__breadcrumb');
    const mobileBreadcrumb = document.querySelector(MOBILE_BREADCRUMB_SELECTOR);
    if (!sourceBreadcrumb || !mobileBreadcrumb) return;

    const sourceCurrent = sourceBreadcrumb.querySelector('.bdk-category-breadcrumb__current');
    if (!sourceCurrent) return;

    const visibleLinks = Array.from(
      sourceBreadcrumb.querySelectorAll('.bdk-category-breadcrumb__link')
    ).filter((link) => !link.hidden);

    let parentSource = visibleLinks[visibleLinks.length - 1] || sourceBreadcrumb.querySelector('.bdk-category-breadcrumb__home');

    if (parentSource) {
      const parentUrl = new URL(parentSource.href, window.location.href);
      const currentUrl = new URL(window.location.href);
      const normalizePath = (path) => path.replace(/\/+$/, '') || '/';
      const sameDestination = (
        normalizePath(parentUrl.pathname) === normalizePath(currentUrl.pathname) &&
        parentUrl.search === currentUrl.search &&
        parentUrl.hash === currentUrl.hash
      );

      if (sameDestination) {
        parentSource = visibleLinks[visibleLinks.length - 2]
          || sourceBreadcrumb.querySelector('.bdk-category-breadcrumb__home');
      }
    }

    const fragment = document.createDocumentFragment();

    if (parentSource) {
      const parent = parentSource.cloneNode(true);
      const parentText = parentSource.textContent.replace(/\s+/g, ' ').trim();

      parent.className = 'bdk-category-breadcrumb__link bdk-collection-mobile-context__parent';
      parent.removeAttribute('hidden');
      parent.removeAttribute('aria-current');
      parent.removeAttribute('data-bdk-breadcrumb-taxonomy-node');
      parent.textContent = `‹ ${parentText}`;
      parent.title = parentText;

      const separator = document.createElement('span');
      separator.className = 'bdk-category-breadcrumb__separator bdk-collection-mobile-context__separator';
      separator.setAttribute('aria-hidden', 'true');
      separator.textContent = '•';

      fragment.append(parent, separator);
    }

    const current = document.createElement('span');
    const currentText = sourceCurrent.textContent.replace(/\s+/g, ' ').trim();

    current.className = 'bdk-category-breadcrumb__current bdk-collection-mobile-context__current';
    current.setAttribute('aria-current', 'page');
    current.setAttribute('data-bdk-category-breadcrumb-current', '');
    current.textContent = currentText;
    current.title = currentText;

    fragment.append(current);
    mobileBreadcrumb.replaceChildren(fragment);
  };

  const updateLocalNav = (navigation) => {
    const scrollable = navigation.scrollWidth > navigation.clientWidth + 1;
    const atStart = navigation.scrollLeft <= 1;
    const atEnd = Math.ceil(navigation.scrollLeft + navigation.clientWidth) >= navigation.scrollWidth - 1;
    const desktopControls = window.matchMedia('(min-width: 990px)').matches;
    const viewport = navigation.closest(LOCAL_NAV_VIEWPORT_SELECTOR);
    const previous = viewport?.querySelector(LOCAL_NAV_PREV_SELECTOR);
    const next = viewport?.querySelector(LOCAL_NAV_NEXT_SELECTOR);

    navigation.classList.toggle('has-overflow', scrollable);
    navigation.classList.toggle('is-start', scrollable && atStart);
    navigation.classList.toggle('is-end', scrollable && atEnd);

    if (previous) previous.hidden = !desktopControls || !scrollable || atStart;
    if (next) next.hidden = !desktopControls || !scrollable || atEnd;
  };

  const scrollLocalNav = (navigation, direction) => {
    const distance = Math.max(240, Math.round(navigation.clientWidth * 0.72));
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    navigation.scrollBy({
      left: distance * direction,
      behavior: reducedMotion ? 'auto' : 'smooth',
    });
  };

  const bindLocalNavControls = (navigation) => {
    const viewport = navigation.closest(LOCAL_NAV_VIEWPORT_SELECTOR);
    if (!viewport || viewport.dataset.bdkLocalNavControlsBound === 'true') return;

    const previous = viewport.querySelector(LOCAL_NAV_PREV_SELECTOR);
    const next = viewport.querySelector(LOCAL_NAV_NEXT_SELECTOR);

    viewport.dataset.bdkLocalNavControlsBound = 'true';
    previous?.addEventListener('click', () => scrollLocalNav(navigation, -1));
    next?.addEventListener('click', () => scrollLocalNav(navigation, 1));
  };

  const centerLocalNav = (navigation) => {
    if (!window.matchMedia(LOCAL_NAV_MOBILE_QUERY).matches) {
      delete navigation.dataset.bdkLocalNavCentered;
      return;
    }

    if (navigation.dataset.bdkLocalNavCentered === 'true') return;

    const active = navigation.querySelector(LOCAL_NAV_ACTIVE_SELECTOR);
    navigation.dataset.bdkLocalNavCentered = 'true';
    if (!active) return;

    const target = Math.max(
      0,
      Math.min(
        active.offsetLeft + active.offsetWidth / 2 - navigation.clientWidth / 2,
        navigation.scrollWidth - navigation.clientWidth
      )
    );

    if (typeof navigation.scrollTo === 'function') navigation.scrollTo({ left: target, behavior: 'auto' });
    else navigation.scrollLeft = target;

    updateLocalNav(navigation);
  };

  const syncLocalNavigation = () => {
    document.querySelectorAll(LOCAL_NAV_SELECTOR).forEach((navigation) => {
      if (navigation.dataset.bdkLocalNavBound !== 'true') {
        navigation.dataset.bdkLocalNavBound = 'true';
        navigation.addEventListener('scroll', () => updateLocalNav(navigation), { passive: true });
      }

      bindLocalNavControls(navigation);
      updateLocalNav(navigation);
      centerLocalNav(navigation);
    });
  };

  const focusCurrentCollectionSliders = () => {
    document.querySelectorAll(COLLECTION_SLIDER_SELECTOR).forEach((slider) => {
      if (slider.dataset.bdkCurrentCollectionFocused === 'true') return;

      const current = slider.querySelector(COLLECTION_SLIDER_CURRENT_SELECTOR);
      if (!current) return;

      slider.dataset.bdkCurrentCollectionFocused = 'true';

      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          if (!slider.isConnected || !current.isConnected) return;

          const maxScroll = Math.max(0, slider.scrollWidth - slider.clientWidth);
          if (maxScroll <= 1) return;

          const sliderRect = slider.getBoundingClientRect();
          const currentRect = current.getBoundingClientRect();
          const currentCenter = slider.scrollLeft + (currentRect.left - sliderRect.left) + currentRect.width / 2;
          const target = Math.max(0, Math.min(currentCenter - slider.clientWidth / 2, maxScroll));

          if (typeof slider.scrollTo === 'function') slider.scrollTo({ left: target, behavior: 'auto' });
          else slider.scrollLeft = target;
        });
      });
    });
  };

  const arrangeInfo = (context) => {
    const info = context.querySelector('[data-bdk-collection-info]') || document.querySelector('[data-bdk-collection-info]');
    if (!info) return;

    const desktopSlot = context.querySelector('[data-bdk-desktop-collection-info-slot]');
    const mobileSlot = document.querySelector('[data-bdk-mobile-collection-info-slot]');
    const destination = window.matchMedia(DESKTOP_QUERY).matches ? desktopSlot : mobileSlot;

    if (destination && info.parentElement !== destination) {
      destination.appendChild(info);
    }
  };

  const refresh = () => {
    syncLocalNavigation();
    focusCurrentCollectionSliders();

    const context = document.querySelector(CONTEXT_SELECTOR);
    if (!context) return;

    syncBreadcrumbDuplicateNode(context);
    syncMobileBreadcrumb(context);
    syncDesktopStickyBar(context);

    const nativeToggle = document.querySelector(NATIVE_FILTER_SELECTOR);
    const proxies = Array.from(document.querySelectorAll(PROXY_SELECTOR));

    proxies.forEach((proxy) => {
      proxy.hidden = !nativeToggle;
      bindProxy(proxy, nativeToggle);
    });
    syncProxyState(nativeToggle, proxies);
    arrangeInfo(context);

    const sidebar = document.querySelector('[data-collection-sidebar]');
    syncRewardsLauncher(sidebar);

    if (sidebar && sidebar.dataset.bdkRewardsObserverBound !== 'true') {
      sidebar.dataset.bdkRewardsObserverBound = 'true';
      const sidebarObserver = new MutationObserver(() => syncRewardsLauncher(sidebar));
      sidebarObserver.observe(sidebar, { attributes: true, attributeFilter: ['class'] });
    }

    if (nativeToggle && nativeToggle.dataset.bdkFilterObserverBound !== 'true') {
      nativeToggle.dataset.bdkFilterObserverBound = 'true';
      const observer = new MutationObserver(() => {
        syncProxyState(nativeToggle, Array.from(document.querySelectorAll(PROXY_SELECTOR)));
      });
      observer.observe(nativeToggle, { attributes: true, subtree: true, childList: true, characterData: true });
    }
  };

  let frame = null;
  const scheduleRefresh = () => {
    if (frame) window.cancelAnimationFrame(frame);
    frame = window.requestAnimationFrame(() => {
      frame = null;
      refresh();
    });
  };

  window.BadinkaCategoryContext = { refresh: scheduleRefresh };
  window.addEventListener('scroll', scheduleDesktopStickyBar, { passive: true });
  window.addEventListener('resize', () => {
    document.querySelectorAll(LOCAL_NAV_SELECTOR).forEach((navigation) => {
      delete navigation.dataset.bdkLocalNavCentered;
    });
    scheduleRefresh();
  }, { passive: true });
  window.addEventListener('bdk:collection-context:refresh', scheduleRefresh);
  document.addEventListener('shopify:section:load', scheduleRefresh);
  document.addEventListener('ptOnLoad', scheduleRefresh);

  if (document.fonts?.ready) {
    document.fonts.ready.then(() => {
      document.querySelectorAll(LOCAL_NAV_SELECTOR).forEach((navigation) => {
        delete navigation.dataset.bdkLocalNavCentered;
      });
      scheduleRefresh();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', refresh, { once: true });
  } else {
    refresh();
  }

})();
